import { useEffect, useState } from "react";
import { Sparkle, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Markdown } from "@/components/AIDrawer";
import { api } from "@/lib/api";

const TYPE_LABELS = {
  portfolio: "Portfolio", campaign: "Campaign", audience: "Audience", creative: "Creative", leads: "Leads",
};

const TYPE_STYLES = {
  portfolio: "bg-ai/10 text-ai border-ai/25",
  campaign: "bg-[#0052FF]/8 text-[#0052FF] border-[#0052FF]/20",
  audience: "bg-success/10 text-[#00953e] border-success/25",
  creative: "bg-warning/10 text-[#b07600] border-warning/30",
  leads: "bg-red-50 text-red-500 border-red-200",
};

export default function Insights() {
  const [insights, setInsights] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get("/ai/insights").then(({ data }) => setInsights(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-4 max-w-4xl">
      <p className="text-sm text-[#525866]">
        {insights ? `${insights.length} saved AI analyses — every decision Claude has made, never lost` : " "}
      </p>
      {!insights ? (
        <div className="space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
      ) : insights.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center text-sm text-[#8a90a0]" data-testid="insights-empty">
          No AI analyses yet — run one from any page's AI button.
        </div>
      ) : (
        insights.map((ins) => {
          const open = openId === ins.id;
          return (
            <div key={ins.id} data-testid={`insight-card-${ins.id}`} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
              <button onClick={() => setOpenId(open ? null : ins.id)} data-testid={`insight-toggle-${ins.id}`}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#FAFAFB] transition-colors">
                <span className="h-8 w-8 shrink-0 rounded-lg bg-ai/10 flex items-center justify-center">
                  <Sparkle size={16} weight="duotone" className="text-ai" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-full font-bold text-[9px] uppercase border ${TYPE_STYLES[ins.type] || TYPE_STYLES.portfolio}`}>
                      {TYPE_LABELS[ins.type] || ins.type}
                    </Badge>
                    <span className="font-heading text-sm font-extrabold text-[#0A0D14] truncate">{ins.entity_name}</span>
                  </div>
                  {!open && <p className="text-xs text-[#8a90a0] mt-1 truncate">{ins.content.replace(/[#*]/g, "").slice(0, 140)}…</p>}
                </div>
                <span className="text-[10px] font-mono text-[#8a90a0] shrink-0">
                  {new Date(ins.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
                {open ? <CaretUp size={14} className="text-[#8a90a0] shrink-0" /> : <CaretDown size={14} className="text-[#8a90a0] shrink-0" />}
              </button>
              {open && (
                <div className="px-5 pb-5 pt-1 border-t border-[#E5E7EB]/60" data-testid={`insight-content-${ins.id}`}>
                  <Markdown text={ins.content} />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
